import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlusCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PropertyList from '@/components/properties/property-list';
import { usePropertiesQuery } from '@/hooks/queries/useProperties';
import { usePropertyTypesDropdownQuery } from '@/hooks/queries/useDropdowns';
import { useCanManageProperties } from '@/hooks/usePermissions';
import type { PropertiesListParams } from '@/types/api';
import { cn } from '@/lib/utils';

export default function PropertiesPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Permission check
  const canManage = useCanManageProperties();

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Debounced search query (500ms delay)
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Build query parameters for API
  const queryParams = useMemo(() => {
    const params: PropertiesListParams = {
      page: currentPage,
      page_size: itemsPerPage,
    };

    if (debouncedSearchQuery) params.search = debouncedSearchQuery;
    if (typeFilter !== 'all') params.property_type = typeFilter;
    if (statusFilter !== 'all')
      params.status = statusFilter === 'active' ? 'true' : 'false';

    return params;
  }, [currentPage, debouncedSearchQuery, typeFilter, statusFilter]);

  // Use API hooks for data
  const {
    data: propertiesResponse,
    isLoading,
    error,
  } = usePropertiesQuery(queryParams);
  const { data: propertyTypesData } = usePropertyTypesDropdownQuery();

  // Ensure propertyTypes is always an array
  const propertyTypes = useMemo(() => {
    if (!propertyTypesData) return [];
    if (Array.isArray(propertyTypesData)) return propertyTypesData;
    return [];
  }, [propertyTypesData]);

  // Calculate pagination info
  const totalCount =
    propertiesResponse?.pagination?.total ||
    propertiesResponse?.pagination?.count ||
    0;
  const totalPages =
    propertiesResponse?.pagination?.last_page ||
    Math.ceil(totalCount / itemsPerPage);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, typeFilter, statusFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
      {/* Header */}
      <div
        className={cn(
          'flex flex-col sm:flex-row items-start sm:items-center gap-4',
          canManage && 'justify-between',
          canManage && (isRTL ? 'sm:flex-row' : 'sm:flex-row-reverse'),
          !canManage && (isRTL ? 'sm:flex-row' : 'sm:flex-row')
        )}
      >
        {canManage && (
          <div className="flex items-center gap-2 w-full sm:w-auto order-2 sm:order-1">
            <Button
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => navigate('/properties/create/1')}
            >
              <PlusCircle className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
              <span className="xs:inline">
                {t('properties.management.addNewProperty')}
              </span>
            </Button>
          </div>
        )}
        <div
          className={cn(
            'space-y-1',
            canManage && 'order-1 sm:order-2',
            isRTL ? 'text-right' : 'text-left'
          )}
        >
          <h1 className="text-2xl sm:text-3xl font-bold font-headline text-primary">
            {t('properties.management.title')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('properties.management.description')}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="pt-4 sm:pt-6">
          <div className="space-y-4">
            {/* Search bar */}
            <div className="relative">
              <Search
                className={cn(
                  'absolute top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4',
                  isRTL ? 'right-3' : 'left-3'
                )}
              />
              {/* Show loading spinner when debouncing */}
              {searchQuery !== debouncedSearchQuery && (
                <div
                  className={cn(
                    'absolute top-1/2 transform -translate-y-1/2 text-muted-foreground',
                    isRTL ? 'left-3' : 'right-3'
                  )}
                >
                  <div className="animate-spin h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full" />
                </div>
              )}
              <Input
                placeholder={t('properties.filters.searchProperties')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={cn(
                  'text-sm',
                  isRTL ? 'pr-9 text-right' : 'pl-9',
                  searchQuery !== debouncedSearchQuery
                    ? isRTL
                      ? 'pl-9'
                      : 'pr-9'
                    : ''
                )}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            {/* Filters - Stack on mobile */}
            <div
              className={cn(
                'flex flex-col sm:flex-row gap-3 sm:gap-4',
                isRTL ? 'sm:flex-row-reverse' : 'sm:flex-row'
              )}
            >
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder={t('properties.propertyType')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('properties.filters.allTypes')}
                  </SelectItem>
                  {propertyTypes.map(type => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder={t('properties.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('properties.filters.allStatus')}
                  </SelectItem>
                  <SelectItem value="active">
                    {t('properties.active')}
                  </SelectItem>
                  <SelectItem value="inactive">
                    {t('properties.inactive')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div
            className={cn(
              'mt-4 text-xs sm:text-sm text-muted-foreground',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            {!isLoading &&
              propertiesResponse &&
              t('properties.filters.showing', {
                count: propertiesResponse.data?.length || 0,
                total: totalCount,
              })}
          </div>
        </CardContent>
      </Card>

      {/* Properties Table */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardHeader className="px-4 sm:px-6">
          <CardTitle
            className={cn(
              'text-lg sm:text-xl font-headline text-primary',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            {t('properties.management.allProperties')}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 sm:px-4 lg:px-6">
          <PropertyList
            properties={propertiesResponse?.data || []}
            isLoading={isLoading}
            error={error}
            pagination={{
              current: currentPage,
              total: totalPages,
              count: totalCount,
              itemsPerPage: itemsPerPage,
            }}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
